import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { resolveMedia } from "../../lib/api";
import { useLenisReduced } from "../../lib/motionFlag";

// Fade / slide-in reveal
export function Reveal({ children, y = 40, delay = 0, className = "", once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-80px" }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// Masked line-by-line reveal for large headlines
export function MaskedLines({ lines, className = "", lineClass = "", delay = 0 }) {
  return (
    <span className={className}>
      {lines.map((ln, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            className={`block ${lineClass}`}
            initial={{ y: "110%" }}
            whileInView={{ y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: delay + i * 0.09, ease: [0.16, 1, 0.3, 1] }}
          >
            {ln}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

// Parallax image inside a clipped frame
export function ParallaxImage({ src, alt = "", className = "", speed = 0.15, rounded = "rounded-2xl", overlay = false, testId }) {
  const ref = useRef(null);
  const reduced = useLenisReduced();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduced ? ["0%", "0%"] : [`${-speed * 100}%`, `${speed * 100}%`]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${rounded} ${className}`} data-testid={testId}>
      <motion.img
        src={resolveMedia(src)}
        alt={alt}
        loading="lazy"
        style={{ y, scale: 1.18 }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      {overlay && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />}
    </div>
  );
}

export function SectionLabel({ children, dark = false, className = "" }) {
  return (
    <div className={`mb-6 flex items-center gap-3 ${className}`}>
      <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
      <span className="h-px w-8 bg-[#FF5722]/40" />
      <span className="font-body text-xs font-bold uppercase tracking-[0.25em] text-[#FF5722]">{children}</span>
    </div>
  );
}

export { motion };
